<template>
  <v-form v-model="formValid" class="px-3 pt-6">
    <v-row>
      <!-- licensePlate -->
      <v-col cols="12" md="6">
        <v-text-field
          v-model="licensePlate"
          :label="$vuetify.lang.t('$vuetify.BOOKING.CAR_NUMBER')"
          placeholder="123ABC"
          outlined
          hide-details
          :rules="licensePlateRules"
          required
          autocomplete="off"
          clearable
          @blur="checkIfVehicleIsParking"
        ></v-text-field>
      </v-col>
      <!-- parking status -->
      <v-col cols="12" md="6" class="d-flex align-center">
        <v-text-field
          :value="getParkingStatus()"
          :label="$vuetify.lang.t('$vuetify.BOOKING.STATUS')"
          :placeholder="$vuetify.lang.t('$vuetify.BOOKING.STATUS')"
          :class="vehicleIsParking ? 'text-success' : 'text-error'"
          outlined
          hide-details
          readonly
          disabled
          autocomplete="off"
        ></v-text-field>
        <v-progress-circular
          v-if="
            loading.state &&
              loading.type === 'getBookingParkingStatusByVehicleNumber'
          "
          style="width: 24px; height: 24px; position: absolute; right: 56px;"
          indeterminate
          color="secondary"
        ></v-progress-circular>
      </v-col>

      <!-- start time date -->
      <v-col cols="12" md="6">
        <v-menu
          v-if="$vuetify.breakpoint.mdAndUp"
          v-model="startTimeDatePicker"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="formattedStartTimeDatePart"
              :value="startTimeDatePart"
              :label="$vuetify.lang.t('$vuetify.BOOKING.START_DATE')"
              :placeholder="$vuetify.lang.t('$vuetify.BOOKING.START_DATE')"
              append-icon="mdi-calendar"
              outlined
              :hide-details="hideStartDateFormatError"
              clearable
              v-bind="attrs"
              :rules="dateFormatRule"
              autocomplete="off"
              @click:clear="startTimeDate = null"
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="startTimeDatePart"
            :value="startTimeDatePart"
            :first-day-of-week="1"
            @input="startTimeDatePicker = false"
          ></v-date-picker>
        </v-menu>
        <v-dialog
          v-if="$vuetify.breakpoint.smAndDown"
          v-model="startTimeDatePicker"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="formattedStartTimeDatePart"
              :value="startTimeDatePart"
              :label="$vuetify.lang.t('$vuetify.BOOKING.START_DATE')"
              :placeholder="$vuetify.lang.t('$vuetify.BOOKING.START_DATE')"
              append-icon="mdi-calendar"
              outlined
              :hide-details="hideStartDateFormatError"
              clearable
              v-bind="attrs"
              :rules="dateFormatRule"
              autocomplete="off"
              @click:clear="startTimeDate = null"
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="startTimeDatePart"
            :value="startTimeDatePart"
            :first-day-of-week="1"
            @input="startTimeDatePicker = false"
          ></v-date-picker>
        </v-dialog>
      </v-col>
      <!-- start time time -->
      <v-col cols="12" md="6">
        <v-menu
          v-if="$vuetify.breakpoint.mdAndUp"
          v-model="startTimeTimePicker"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="startTimeTimePart"
              :label="$vuetify.lang.t('$vuetify.BOOKING.START_TIME')"
              :placeholder="$vuetify.lang.t('$vuetify.BOOKING.START_TIME')"
              append-icon="mdi-clock"
              outlined
              clearable
              v-bind="attrs"
              :rules="timeFormatRule"
              :hide-details="hideStartTimeFormatError"
              autocomplete="off"
              @click:clear="startTimeTimePart = ''"
              v-on="on"
              @blur="updatePriceInfo"
            ></v-text-field>
          </template>
          <v-time-picker
            ref="startTimeTimePicker"
            v-model="startTimeTimePart"
            format="24hr"
          >
            <template v-slot:default>
              <div class="d-flex justify-end">
                <v-btn text color="primary" class="secondary--text" @click="cancelStartTimeTimePicker">
                  {{ $vuetify.lang.t('$vuetify.CANCEL') }}
                </v-btn>
                <v-btn color="primary" class="secondary--text" @click="startTimeTimePicker = false">
                  OK
                </v-btn>
              </div>
            </template>
          </v-time-picker>
        </v-menu>
        <v-dialog
          v-if="$vuetify.breakpoint.smAndDown"
          v-model="startTimeTimePicker"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="startTimeTimePart"
              :label="$vuetify.lang.t('$vuetify.BOOKING.START_TIME')"
              :placeholder="$vuetify.lang.t('$vuetify.BOOKING.START_TIME')"
              append-icon="mdi-clock"
              outlined
              clearable
              v-bind="attrs"
              :rules="timeFormatRule"
              :hide-details="hideStartTimeFormatError"
              autocomplete="off"
              @click:clear="startTimeTimePart = ''"
              v-on="on"
              @blur="updatePriceInfo"
            ></v-text-field>
          </template>
          <v-time-picker
            ref="startTimeTimePicker"
            v-model="startTimeTimePart"
            format="24hr"
          >
            <template v-slot:default>
              <v-btn text color="primary" class="secondary--text" @click="cancelStartTimeTimePicker">
                {{ $vuetify.lang.t('$vuetify.CANCEL') }}
              </v-btn>
              <v-btn color="primary" class="secondary--text" @click="startTimeTimePicker = false">
                OK
              </v-btn>
            </template>
          </v-time-picker>
        </v-dialog>
      </v-col>

      <!-- end time date -->
      <v-col cols="12" md="6">
        <v-menu
          v-if="$vuetify.breakpoint.mdAndUp"
          v-model="endTimeDatePicker"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="formattedEndTimeDatePart"
              :value="endTimeDatePart"
              :label="$vuetify.lang.t('$vuetify.BOOKING.END_DATE')"
              :placeholder="$vuetify.lang.t('$vuetify.BOOKING.END_DATE')"
              append-icon="mdi-calendar"
              outlined
              clearable
              v-bind="attrs"
              :hide-details="hideEndDateFormatError"
              :rules="dateFormatRule"
              autocomplete="off"
              @click:clear="endTimeDatePart = null"
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="endTimeDatePart"
            :value="endTimeDatePart"
            :first-day-of-week="1"
            @input="endTimeDatePicker = false"
          ></v-date-picker>
        </v-menu>
        <v-dialog
          v-if="$vuetify.breakpoint.smAndDown"
          v-model="endTimeDatePicker"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="formattedEndTimeDatePart"
              :value="endTimeDatePart"
              :label="$vuetify.lang.t('$vuetify.BOOKING.END_DATE')"
              :placeholder="$vuetify.lang.t('$vuetify.BOOKING.END_DATE')"
              append-icon="mdi-calendar"
              outlined
              clearable
              v-bind="attrs"
              :hide-details="hideEndDateFormatError"
              :rules="dateFormatRule"
              autocomplete="off"
              @click:clear="endTimeDatePart = null"
              v-on="on"
            ></v-text-field>
          </template>
          <v-date-picker
            v-model="endTimeDatePart"
            :value="endTimeDatePart"
            :first-day-of-week="1"
            @input="endTimeDatePicker = false"
          ></v-date-picker>
        </v-dialog>
      </v-col>
      <!-- end time time -->
      <v-col cols="12" md="6">
        <v-menu
          v-if="$vuetify.breakpoint.mdAndUp"
          v-model="endTimeTimePicker"
          :close-on-content-click="false"
          transition="scale-transition"
          offset-y
          min-width="290px"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="endTimeTimePart"
              :label="$vuetify.lang.t('$vuetify.BOOKING.END_TIME')"
              :placeholder="$vuetify.lang.t('$vuetify.BOOKING.END_TIME')"
              append-icon="mdi-clock"
              outlined
              clearable
              v-bind="attrs"
              :rules="timeFormatRule"
              :hide-details="hideEndTimeFormatError"
              autocomplete="off"
              @click:clear="endTimeTimePart = ''"
              v-on="on"
              @blur="updatePriceInfo"
            ></v-text-field>
          </template>
          <v-time-picker
            ref="endTimeTimePicker"
            v-model="endTimeTimePart"
            format="24hr"
          >
            <template v-slot:default>
              <div class="d-flex justify-end">
                <v-btn text color="primary" class="secondary--text" @click="cancelEndTimeTimePicker">
                  {{ $vuetify.lang.t('$vuetify.CANCEL') }}
                </v-btn>
                <v-btn color="primary" class="secondary--text" @click="endTimeTimePicker = false">
                  OK
                </v-btn>
              </div>
            </template>
          </v-time-picker>
        </v-menu>
        <v-dialog
          v-if="$vuetify.breakpoint.smAndDown"
          v-model="endTimeTimePicker"
        >
          <template v-slot:activator="{ on, attrs }">
            <v-text-field
              v-model="endTimeTimePart"
              :label="$vuetify.lang.t('$vuetify.BOOKING.END_TIME')"
              :placeholder="$vuetify.lang.t('$vuetify.BOOKING.END_TIME')"
              append-icon="mdi-clock"
              outlined
              clearable
              v-bind="attrs"
              :rules="timeFormatRule"
              :hide-details="hideEndTimeFormatError"
              autocomplete="off"
              @click:clear="endTimeTimePart = ''"
              v-on="on"
              @blur="updatePriceInfo"
            ></v-text-field>
          </template>
          <!-- Time picker -->
          <v-time-picker
            ref="endTimeTimePicker"
            v-model="endTimeTimePart"
            format="24hr"
          >
            <template v-slot:default>
              <div class="d-flex justify-end">
                <v-btn text color="primary" class="secondary--text" @click="cancelEndTimeTimePicker">
                  {{ $vuetify.lang.t('$vuetify.CANCEL') }}
                </v-btn>
                <v-btn color="primary" class="secondary--text" @click="endTimeTimePicker = false">
                  OK
                </v-btn>
              </div>
            </template>
          </v-time-picker>
        </v-dialog>
      </v-col>

      <!-- name -->
      <v-col cols="12" md="6">
        <v-text-field
          v-model="name"
          :label="$vuetify.lang.t('$vuetify.BOOKING.NAME')"
          placeholder="John Doe"
          outlined
          hide-details
          autocomplete="off"
          clearable
        ></v-text-field>
      </v-col>
      <!-- phone -->
      <v-col cols="12" md="6">
        <v-text-field
          v-model="phone"
          :label="$vuetify.lang.t('$vuetify.BOOKING.PHONE_NUMBER')"
          placeholder="5555555"
          outlined
          hide-details
          type="number"
          autocomplete="off"
          clearable
        ></v-text-field>
      </v-col>
      <!-- email -->
      <v-col cols="12" md="6">
        <v-text-field
          v-model="email"
          :label="$vuetify.lang.t('$vuetify.BOOKING.EMAIL')"
          placeholder="johndoe@example.com"
          outlined
          hide-details
          type="email"
          autocomplete="off"
          clearable
        ></v-text-field>
      </v-col>
      <!-- external booking number -->
      <v-col cols="12" md="6">
        <v-text-field
          v-model="externalBookingNumber"
          :label="$vuetify.lang.t('$vuetify.BOOKING.EXTERNAL_BOOKING_NUMBER')"
          :placeholder="
            $vuetify.lang.t('$vuetify.BOOKING.EXTERNAL_BOOKING_NUMBER')
          "
          outlined
          hide-details
          autocomplete="off"
          clearable
        ></v-text-field>
      </v-col>
      <!-- comments -->
      <v-col cols="12" md="12">
        <v-text-field
          v-model="comments"
          :label="$vuetify.lang.t('$vuetify.BOOKING.COMMENTS')"
          :placeholder="$vuetify.lang.t('$vuetify.BOOKING.COMMENTS')"
          outlined
          hide-details
          autocomplete="off"
          clearable
        ></v-text-field>
      </v-col>
    </v-row>
    <v-row v-if="selectedBookingProfile && selectedBookingProfile.ShouldCalculateCost">
      <v-col class="d-flex justify-end">
        <h3>
          {{ `${$vuetify.lang.t('$vuetify.PRICE')}` }}:&nbsp;
          {{ priceInfoForSelectedPeriod }}
        </h3>
        <!-- <v-progress-circular
          v-if="updatingPriceInfo"
          style="width: 24px; height: 24px; position: absolute; right: 56px;"
          indeterminate
          color="secondary"
        ></v-progress-circular> -->
      </v-col>
    </v-row>
  </v-form>
</template>
<script>
import { mapGetters, mapMutations, mapActions } from 'vuex'

export default {
  name: 'BookingForm',
  data() {
    return {
      formValid: false,
      licensePlateRules: [
        v =>
          v.length >= 2 ||
          this.$vuetify.lang.t('$vuetify.BOOKING.CAR_IS_REQUIRED')
      ],
      timeFormatRule: [
        v =>
          /^(?:[01]\d|2[0-3]):[0-5]\d$/.test(v) ||
          this.$vuetify.lang.t('$vuetify.BOOKING.TIME_FORMAT_INCORRECT')
      ],
      dateFormatRule: [
        v =>
          /^\d{2}\.\d{2}\.\d{4}$/.test(v) ||
          this.$vuetify.lang.t('$vuetify.BOOKING.DATE_FORMAT_INCORRECT')
      ],
      startTimeDatePicker: false,
      endTimeDatePicker: false,
      startTimeTimePicker: false,
      endTimeTimePicker: false,
      vehicleIsParking: undefined,
      startTimeDatePart: null,
      startTimeTimePart: null,
      initialStartTimeTimePart: null,
      endTimeDatePart: null,
      endTimeTimePart: null,
      initialEndTimeTimePart: null,
      priceInfoForSelectedPeriod: '-',
      updatingPriceInfo: false
    }
  },
  computed: {
    ...mapGetters('app', ['loading']),
    ...mapGetters('booking', ['selectedBooking', 'selectedBookingProfile']),
    licensePlate: {
      get() {
        return this.selectedBooking.LicensePlate
      },
      set(value) {
        this.setSelectedBookingValue({
          key: 'LicensePlate',
          value
        })
      }
    },
    startTime: {
      get() {
        return this.selectedBooking.StartTime
      },
      set(value) {
        this.setSelectedBookingValue({
          key: 'StartTime',
          value
        })
      }
    },
    endTime: {
      get() {
        return this.selectedBooking.EndTime
      },
      set(value) {
        this.setSelectedBookingValue({
          key: 'EndTime',
          value
        })
      }
    },
    formattedStartTimeDatePart: {
      get() {
        if (this.startTimeDatePart) {
          const parts = this.startTimeDatePart.split('-')
          return `${parts[2]}.${parts[1]}.${parts[0]}`
        }
        return null
      },
      set(value) {
        try {
          const parts = value.split('.')
          if (Array.isArray(parts) && parts.length === 3) {
            const day = parts[0]
            const month = parts[1]
            const year = parts[2]
            this.startTimeDatePart = `${year}-${month}-${day}`
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.log(err)
        }
      }
    },
    formattedEndTimeDatePart: {
      get() {
        if (this.endTimeDatePart) {
          const parts = this.endTimeDatePart.split('-')
          return `${parts[2]}.${parts[1]}.${parts[0]}`
        }
        return null
      },
      set(value) {
        try {
          const parts = value.split('.')
          if (Array.isArray(parts) && parts.length === 3) {
            const day = parts[0]
            const month = parts[1]
            const year = parts[2]
            this.endTimeDatePart = `${year}-${month}-${day}`
          }
        } catch (err) {
          // eslint-disable-next-line no-console
          console.error(err)
        }
      }
    },
    name: {
      get() {
        return this.selectedBooking.Name
      },
      set(value) {
        this.setSelectedBookingValue({
          key: 'Name',
          value
        })
      }
    },
    phone: {
      get() {
        return this.selectedBooking.Phone
      },
      set(value) {
        this.setSelectedBookingValue({
          key: 'Phone',
          value
        })
      }
    },
    email: {
      get() {
        return this.selectedBooking.Email
      },
      set(value) {
        this.setSelectedBookingValue({
          key: 'Email',
          value
        })
      }
    },
    externalBookingNumber: {
      get() {
        return this.selectedBooking.ExternalBookingNumber
      },
      set(value) {
        this.setSelectedBookingValue({
          key: 'ExternalBookingNumber',
          value
        })
      }
    },
    comments: {
      get() {
        return this.selectedBooking.Comments
      },
      set(value) {
        this.setSelectedBookingValue({
          key: 'Comments',
          value
        })
      }
    },
    hideStartTimeFormatError() {
      if (/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(this.startTimeTimePart)) {
        return true
      }
      return false
    },
    hideEndTimeFormatError() {
      if (/^(?:[01]\d|2[0-3]):[0-5]\d$/.test(this.endTimeTimePart)) {
        return true
      }
      return false
    },
    hideStartDateFormatError() {
      if (/^\d{2}\.\d{2}\.\d{4}$/.test(this.formattedStartTimeDatePart)) {
        return true
      }
      if (!this.startTimeDatePart) {
        return true
      }
      return false
    },
    hideEndDateFormatError() {
      if (/^\d{2}\.\d{2}\.\d{4}$/.test(this.formattedEndTimeDatePart)) {
        return true
      }
      if (!this.endTimeDatePart) {
        return true
      }
      return false
    }
  },
  watch: {
    formValid() {
      this.$emit(this.formValid ? 'valid' : 'invalid')

      if (this.formValid) {
        this.updatePriceInfo()
      }
    },
    startTimeDatePart(newValue) {
      if (!this.startTimeTimePart) return
      const [hours, minutes] = this.startTimeTimePart.split(':').map(Number)
      const dt = new Date(newValue)
      dt.setHours(hours)
      dt.setMinutes(minutes)
      this.setSelectedBookingValue({
        key: 'StartTime',
        value: dt
      })
    },
    startTimeTimePart() {
      const dt = new Date(this.startTimeDatePart)
      if (dt instanceof Date) {
        const [hours, minutes] = this.startTimeTimePart.split(':').map(Number)
        dt.setHours(hours)
        dt.setMinutes(minutes)
        this.setSelectedBookingValue({
          key: 'StartTime',
          value: dt
        })
      }
    },
    endTimeDatePart(newValue) {
      if (!this.endTimeTimePart) return
      const [hours, minutes] = this.endTimeTimePart.split(':').map(Number)
      const dt = new Date(newValue)
      dt.setHours(hours)
      dt.setMinutes(minutes)
      this.setSelectedBookingValue({
        key: 'EndTime',
        value: dt
      })
    },
    endTimeTimePart() {
      const dt = new Date(this.endTimeDatePart)
      if (dt instanceof Date) {
        const [hours, minutes] = this.endTimeTimePart.split(':').map(Number)
        dt.setHours(hours)
        dt.setMinutes(minutes)
        this.setSelectedBookingValue({
          key: 'EndTime',
          value: dt
        })
      }
    },
    startTimeTimePicker(newVal, oldVal) {
      if (oldVal === true && newVal === false) {
        this.$nextTick(() => {
          if (this.$refs.startTimeTimePicker) {
            this.$refs.startTimeTimePicker.selectingHour = true
            this.$refs.startTimeTimePicker.selectingMinutes = false
          }
        })
      }
    },
    endTimeTimePicker(newVal, oldVal) {
      if (oldVal === true && newVal === false) {
        this.$nextTick(() => {
          if (this.$refs.endTimeTimePicker) {
            this.$refs.endTimeTimePicker.selectingHour = true
            this.$refs.endTimeTimePicker.selectingMinutes = false
          }
        })
      }
    },
    startTimeDatePicker(newVal) {
      if (newVal === false) {
        this.updatePriceInfo()
      }
    },
    endTimeDatePicker(newVal) {
      if (newVal === false) {
        this.updatePriceInfo()
      }
    }
  },
  created() {
    if (
      this.selectedBooking &&
      this.selectedBooking.ID > 0 &&
      this.selectedBooking.StartTime
    ) {
      this.startTimeDatePart = this.getDatePart(this.selectedBooking.StartTime)
      this.startTimeTimePart = this.getTimePart(this.selectedBooking.StartTime)
      this.initialStartTimeTimePart = this.startTimeTimePart
    } else if (!this.selectedBooking.ID) {
      this.startTimeDatePart = this.getDatePart(new Date())
    }
    if (
      this.selectedBooking &&
      this.selectedBooking.ID > 0 &&
      this.selectedBooking.EndTime
    ) {
      // Set end time date part and time part
      this.endTimeDatePart = this.getDatePart(this.selectedBooking.EndTime)
      this.endTimeTimePart = this.getTimePart(this.selectedBooking.EndTime)
      this.initialEndTimeTimePart = this.endTimeTimePart
    } else if (!this.selectedBooking.ID) {
      const now = new Date()
      this.endTimeDatePart = this.getDatePart(now.setDate(now.getDate() + 1))
    }

    // Set default start and end hours if exists and needed
    if (
      this.selectedBooking.ID <= 0 &&
      this.selectedBookingProfile &&
      this.selectedBookingProfile.DefaultStartTimeHour
    ) {
      const startHour = String(this.selectedBookingProfile.DefaultStartTimeHour).padStart(2, '0')
      const startMin = String(this.selectedBookingProfile.DefaultStartTimeMinutes || 0).padStart(2, '0')
      this.startTimeTimePart = `${startHour}:${startMin}`
      this.initialStartTimeTimePart = this.startTimeTimePart
    }
    if (
      this.selectedBooking.ID <= 0 &&
      this.selectedBookingProfile &&
      this.selectedBookingProfile.DefaultEndTimeHour
    ) {
      this.endTimeTimePart = `${String(this.selectedBookingProfile.DefaultEndTimeHour).padStart(2, '0')}:00`
      this.initialEndTimeTimePart = this.endTimeTimePart
    }

    if (this.selectedBooking.CostInCents) {
      this.priceInfoForSelectedPeriod = `${Math.round(
        this.selectedBooking.CostInCents / 100
      ).toFixed(2)} EUR`
    }
  },
  mounted() {
    this.$nextTick(() => {
      this.updatePriceInfo()
    })
  },
  methods: {
    ...mapMutations('booking', ['setSelectedBookingValue']),
    ...mapActions('booking', [
      'getBookingParkingStatusByVehicleNumber',
      'getPriceForBookingInPlace'
    ]),
    getDatePart(date) {
      try {
        const dt = date instanceof Date ? date : new Date(date)
        const day = String(dt.getDate()).padStart(2, '0')
        const month = String(dt.getMonth() + 1).padStart(2, '0')
        const year = dt.getFullYear()
        return `${year}-${month}-${day}`
      } catch {
        return ''
      }
    },
    getTimePart(date) {
      try {
        const dt = date instanceof Date ? date : new Date(date)
        const hour = String(dt.getHours()).padStart(2, '0')
        const minute = String(dt.getMinutes()).padStart(2, '0')
        return `${hour}:${minute}`
      } catch {
        return ''
      }
    },
    checkIfVehicleIsParking() {
      if (!this.licensePlate || this.licensePlate === '') {
        return
      }
      this.getBookingParkingStatusByVehicleNumber({
        this: this,
        vehicle: this.licensePlate,
        placeId: this.selectedBookingProfile.PlaceID
      })
        .then(r => {
          this.vehicleIsParking = r.isParking
          if (r.licensePlate) {
            this.licensePlate = r.licensePlate
          }
          if (r.isParking) {
            this.startTimeDatePart = this.getDatePart(r.startTime)
            this.startTimeTimePart = this.getTimePart(r.startTime)
          }
        })
        .catch(() => {
          this.vehicleIsParking = false
        })
    },
    getParkingStatus() {
      if (
        !this.licensePlate ||
        this.licensePlate === '' ||
        this.vehicleIsParking === undefined
      ) {
        return ''
      }
      return this.vehicleIsParking
        ? this.$vuetify.lang.t('$vuetify.BOOKING.PARKING_STATUS_PARKING')
        : this.$vuetify.lang.t('$vuetify.BOOKING.PARKING_STATUS_NOT_PARKING')
    },
    cancelStartTimeTimePicker() {
      this.startTimeTimePicker = false
      this.startTimeTimePart = this.initialStartTimeTimePart
    },
    cancelEndTimeTimePicker() {
      this.endTimeTimePicker = false
      this.endTimeTimePart = this.initialEndTimeTimePart
    },
    updatePriceInfo() {
      if (!this.selectedBookingProfile || !this.selectedBookingProfile.ShouldCalculateCost) {
        return
      }

      const start = this.formatDateTime(this.startTime)
      const end = this.formatDateTime(this.endTime)

      if (!start || !end) {
        return
      }

      this.updatingPriceInfo = true

      this.getPriceForBookingInPlace({
        this: this,
        bookingProfileId: this.selectedBookingProfile.BookingProfileID,
        start,
        end
      })
        .then(r => {
          this.priceInfoForSelectedPeriod = `${Math.round(
            r.CostInCents / 100
          ).toFixed(2)} ${r.Currency}`
        })
        .finally(() => {
          this.updatingPriceInfo = false
        })
    },
    formatDateTime(datetime) {
      if (!datetime) return null
      const dt = new Date(datetime)
      const year = dt.getFullYear()
      const month = String(dt.getMonth() + 1).padStart(2, '0')
      const day = String(dt.getDate()).padStart(2, '0')
      const hours = String(dt.getHours()).padStart(2, '0')
      const minutes = String(dt.getMinutes()).padStart(2, '0')
      const seconds = String(dt.getSeconds()).padStart(2, '0')
      return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`
    }
  }
}
</script>

<style>
.text-success input {
  color: #4caf50 !important;
}
.text-error input {
  color: #c8697e !important;
}
.v-picker .v-card__actions {
  justify-content: flex-end;
}
</style>
